import React, { useState, useEffect, useRef } from "react";
import "./PackagesCharts.css";
//components
import DashboardFilter from "../fragments/DashboardFilter";
import Donut from "../../../components/Charts/Donut/Donut";
import Bar from "../../../components/Charts/Bar/Bar";
//services
import { dashboardService } from "../../../services/Dashboard";
//utils
import { formatShortDate } from "../../../utils/utils";
import HeaderSection from "../../../components/PdfDownload/Header";

const PackagesCharts = () => {
  const chartRef = useRef(null);
  const hiddenRef = useRef(null);
  const customColors = [
    "#15B79E",
    "#0E5F79",
    "#F2994A",
    "#7C3AED",
    "#EF4444",
    "#10B981",
    "#3B82F6",
  ];

  const [packages, setPackages] = useState([]);
  const [packageGainSeries, setPackageGainSeries] = useState([]); // series dinámicas por año
  const [totalPackages, setTotalPackages] = useState(0);
  const [reportRange, setReportRange] = useState({ start: null, end: null });

  const currentYear = new Date().getFullYear();
  const lastYear = currentYear - 1;

  useEffect(() => {
    // Estado inicial, dos años por defecto
    const defaultFilters = { years: [currentYear, lastYear] };
    fetchMostPopularPackages(defaultFilters);
    fetchPackageGainForYears(defaultFilters);
  }, []);

  // Normaliza years, acepta numbers o objetos {label, value}
  const normalizeYears = (years) => {
    if (!Array.isArray(years) || years.length === 0) return [];
    return years
      .map((y) => (typeof y === "object" ? Number(y.value) : Number(y)))
      .filter(Boolean);
  };

  // Construye rango YYYY-MM-DD para un año, opcionalmente usando mismo mes/día del rangeDate
  const buildYearWindow = (year, rangeDate) => {
    if (rangeDate?.start_date && rangeDate?.end_date) {
      const startDate = rangeDate.start_date.split("T")[0];
      const endDate = rangeDate.end_date.split("T")[0];
      const [, startMonth, startDay] = startDate.split("-");
      const [, endMonth, endDay] = endDate.split("-");
      const formattedStartMonth = startMonth.padStart(2, "0");
      const formattedStartDay = startDay.padStart(2, "0");
      const formattedEndMonth = endMonth.padStart(2, "0");
      const formattedEndDay = endDay.padStart(2, "0");

      return {
        start_date: `${year}-${formattedStartMonth}-${formattedStartDay}`,
        end_date: `${year}-${formattedEndMonth}-${formattedEndDay}`,
      };
    }
    return { start_date: `${year}-01-01`, end_date: `${year}-12-31` };
  };

  const fetchMostPopularPackages = async (filters = {}) => {
    try {
      const years = normalizeYears(filters.years);
      let start_date = null;
      let end_date = null;

      // Regla: si hay rangeDate, manda sobre years (cámbialo si prefieres combinar)
      if (filters.rangeDate?.start_date && filters.rangeDate?.end_date) {
        start_date = formatShortDate(filters.rangeDate.start_date, "-");
        end_date = formatShortDate(filters.rangeDate.end_date, "-");
      } else if (years.length > 0) {
        // Abarcar todo el rango de años seleccionados
        const minY = Math.min(...years);
        const maxY = Math.max(...years);
        start_date = `${minY}-01-01`;
        end_date = `${maxY}-12-31`;
      }

      const group_by = filters.period
        ? filters.period.value || filters.period
        : null;
      const params = {
        ...(start_date && { start_date }),
        ...(end_date && { end_date }),
        ...(group_by && { group_by }),
      };

      if (start_date && end_date) {
        setReportRange({ start: start_date, end: end_date });
      }

      const response = await dashboardService.mostPopularPackages(params);
      if (response?.series?.length) {
        const data = response.series.map((item) => ({
          x: item.package_name,
          y: item.purchases,
        }));
        setPackages(data);
        console.log("response", response);
        console.log("total", response.total_purchases);
        setTotalPackages(response.total_purchases);
      } else {
        setPackages([]);
        setTotalPackages(0);
      }
    } catch (error) {
      console.error("Error fetching most popular packages:", error);
      setPackages([]);
      setTotalPackages(0);
    }
  };

  // Genera una serie por cada año seleccionado
  const fetchPackageGainForYears = async (filters = {}) => {
    try {
      const years = normalizeYears(filters.years);
      const usedYears = years.length > 0 ? years : [currentYear, lastYear];
      const group_by = filters.period
        ? filters.period.value || filters.period
        : null;

      // Llamadas en paralelo por año
      const calls = usedYears.map(async (year) => {
        const { start_date, end_date } = buildYearWindow(
          year,
          filters.rangeDate,
        );
        const params = {
          ...(start_date && { start_date }),
          ...(end_date && { end_date }),
          ...(group_by && { group_by }),
        };
        setReportRange({ start: start_date, end: end_date });
        const response = await dashboardService.packageGain(params);
        const data = Array.isArray(response?.series)
          ? response.series.map((item) => ({
              x: item.package_name,
              y: item.revenue,
            }))
          : [];
        return { name: String(year), data };
      });

      const results = await Promise.all(calls);
      setPackageGainSeries(results);
    } catch (error) {
      console.error("Error fetching package gain (years):", error);
      setPackageGainSeries([]);
    }
  };

  return (
    <>
      <div className="packages-charts-container">
        <DashboardFilter
          chartRef={hiddenRef}
          includeYearFilter={true}
          onApplyFilters={(filters) => {
            fetchMostPopularPackages(filters);
            fetchPackageGainForYears(filters);
          }}
        />
        <div className="chart-wrapper" ref={chartRef}>
          <div className="chart-card">
            <Bar
              series={packageGainSeries}
              title="Package gain"
              yLabel="Gain"
              yUnit="Revenue"
              height={270}
              width={600}
              colors={customColors}
              maxLabelLength={20}
              tickLabelAngle={20}
              tickFontSize={8}
              widthContainer="100%"
              heightContainer="100%"
              yAsPercent={true}
              yPercentNormalizeToMax={true}
              yPercentDecimals={1}
            />
          </div>

          <div className="chart-card">
            <Donut
              data={packages}
              isTotal={false}
              total={totalPackages}
              title="Most popular packages"
              height={170}
              width={400}
              widthContainer="100%"
              heightContainer="100%"
              innerRadius={40}
              outerRadius={80}
              colors={customColors}
            />
          </div>
        </div>
      </div>

      {/* Bloque oculto solo para PDF */}
      <div ref={hiddenRef} className="hidden-container">
        <HeaderSection
          title="Packages"
          date={formatShortDate(new Date(), "-")}
          period={
            reportRange.start && reportRange.end
              ? `${reportRange.start} - ${reportRange.end}`
              : "Sin fecha"
          }
        />
        <div className="chart-wrapper">
          <div className="chart-card">
            <Bar
              series={packageGainSeries}
              title="Package gain"
              yLabel="Gain"
              yUnit="Packages"
              height={270}
              width={600}
              colors={customColors}
              maxLabelLength={20}
              tickLabelAngle={20}
              tickFontSize={7}
              widthContainer="100%"
              heightContainer="100%"
              yAsPercent={true}
              yPercentNormalizeToMax={true}
              yPercentDecimals={1}
            />
          </div>

          <div className="chart-card">
            <Donut
              data={packages}
              isTotal={true}
              total={totalPackages}
              title="Most popular packages"
              height={170}
              width={400}
              widthContainer="100%"
              heightContainer="100%"
              innerRadius={40}
              outerRadius={80}
              colors={customColors}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default PackagesCharts;
